import React, { useState } from 'react';
import { SettingsIcon, XIcon } from 'lucide-react';
import { SLIPPAGE_OPTIONS } from '../config/constants';

const SlippageSettings = ({ slippage, onSlippageChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customSlippage, setCustomSlippage] = useState('');

  const handlePresetSlippage = (value) => {
    onSlippageChange(value);
    setCustomSlippage('');
  };

  const handleCustomSlippageSubmit = () => {
    const value = parseFloat(customSlippage);
    if (value && value > 0 && value <= 50) {
      onSlippageChange(value);
    }
  };

  const handleCustomSlippageKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCustomSlippageSubmit();
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors duration-200 border border-white/10 hover:border-white/20"
        title={`Slippage Settings (Current: ${slippage}%)`}
      >
        <SettingsIcon className="w-4 h-4 text-white/80" />
        <span className="text-sm text-white/80 font-medium">{slippage}%</span>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-gray-900/95 backdrop-blur-lg border border-white/30 rounded-xl shadow-2xl z-50 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Slippage Settings</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-lg hover:bg-white/15 transition-colors duration-200"
            >
              <XIcon className="w-5 h-5 text-white/60" />
            </button>
          </div>

          <div className="mb-4">
            <p className="text-sm text-white/60 mb-3">
              Slippage tolerance determines the maximum price movement you're willing to accept.
            </p>
            
            <div className="grid grid-cols-3 gap-2 mb-3">
              {SLIPPAGE_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => handlePresetSlippage(option.value)}
                  className={`
                    px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                    ${slippage === option.value 
                      ? 'bg-blue-500/30 border-blue-400/60 text-blue-200' 
                      : 'bg-white/10 border-white/20 text-white/80 hover:bg-white/15 hover:border-white/30'
                    }
                    border
                  `}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="number"
                placeholder="Custom"
                value={customSlippage}
                onChange={(e) => setCustomSlippage(e.target.value)}
                onKeyPress={handleCustomSlippageKeyPress}
                min="0"
                max="50"
                step="0.1"
                className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-white/40"
              />
              <span className="text-white/60 text-sm">%</span>
              <button
                onClick={handleCustomSlippageSubmit}
                disabled={!customSlippage || parseFloat(customSlippage) <= 0 || parseFloat(customSlippage) > 50}
                className="px-3 py-2 bg-blue-500/20 border border-blue-400/50 text-blue-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-500/30 transition-colors duration-200"
              >
                Set
              </button>
            </div>
          </div>

          <div className="bg-white/10 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">Current Slippage:</span>
              <span className="text-white font-medium">{slippage}%</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-white/60">Transaction Fee:</span>
              <span className="text-white font-medium">1.0%</span>
            </div>
          </div>

          {slippage > 5 && (
            <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-400/20 rounded-lg">
              <p className="text-yellow-300 text-sm">
                ⚠️ High slippage tolerance may result in unfavorable trades
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SlippageSettings;